(() => {
  if (window.DeebAccessoryMotion) return;

  const ROOT_SELECTOR =
    '[data-deeb-product-family-root][data-product-family="accessory"]';
  const REVEAL_SELECTOR = '[data-deeb-accessory-reveal]';

  class AccessoryMotionController {
    constructor(root) {
      this.root = root;
      this.items = Array.from(root.querySelectorAll(REVEAL_SELECTOR));
      this.observer = null;
      this.pendingCount = 0;
      this.destroyed = false;

      this.root.dataset.deebAccessoryMotionInitialized = 'true';
      this.initialize();
    }

    initialize() {
      if (this.items.length === 0) {
        this.root.classList.add('deeb-accessory-motion-ready');
        return;
      }

      const reduceMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      if (reduceMotion || !('IntersectionObserver' in window)) {
        this.revealImmediately();
        return;
      }

      const pendingItems = this.items.filter((item) => {
        const rect = item.getBoundingClientRect();
        const visibleNow = rect.top < window.innerHeight * 0.92 && rect.bottom > 0;

        if (visibleNow) item.classList.add('is-visible');
        return !visibleNow;
      });

      this.root.classList.add('deeb-accessory-motion-ready');

      if (pendingItems.length === 0) return;

      this.pendingCount = pendingItems.length;
      this.observer = new IntersectionObserver(this.handleIntersection, {
        threshold: 0.12,
        rootMargin: '0px 0px -8% 0px',
      });

      pendingItems.forEach((item) => this.observer.observe(item));
    }

    handleIntersection = (entries) => {
      if (this.destroyed || !this.observer) return;

      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        entry.target.classList.add('is-visible');
        this.observer.unobserve(entry.target);
        this.pendingCount = Math.max(0, this.pendingCount - 1);
      });

      if (this.pendingCount === 0) {
        this.observer.disconnect();
        this.observer = null;
      }
    };

    revealImmediately() {
      this.items.forEach((item) => item.classList.add('is-visible'));
      this.root.classList.add('deeb-accessory-motion-ready');
    }

    destroy() {
      if (this.destroyed) return;

      this.observer?.disconnect();
      this.observer = null;
      this.pendingCount = 0;
      this.destroyed = true;

      if (this.root) {
        delete this.root.dataset.deebAccessoryMotionInitialized;
      }

      this.root = null;
      this.items = [];
    }
  }

  const instances = new WeakMap();
  const activeInstances = new Set();

  const destroyRoot = (root) => {
    const instance = instances.get(root);
    if (!instance) return;

    instance.destroy();
    activeInstances.delete(instance);
    instances.delete(root);
  };

  const initializeRoot = (root) => {
    if (!(root instanceof HTMLElement) || !root.matches(ROOT_SELECTOR)) return null;

    const existing = instances.get(root);
    if (existing) return existing;

    const instance = new AccessoryMotionController(root);
    instances.set(root, instance);
    activeInstances.add(instance);
    return instance;
  };

  const initializeAll = (scope = document) => {
    if (scope instanceof HTMLElement && scope.matches(ROOT_SELECTOR)) initializeRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initializeRoot);
  };

  const destroyAll = (scope = document) => {
    const roots = [];

    if (scope instanceof HTMLElement && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));

    roots.forEach(destroyRoot);
  };

  const handleSectionLoad = (event) => initializeAll(event.target);
  const handleSectionUnload = (event) => destroyAll(event.target);
  const handleDOMContentLoaded = () => initializeAll(document);

  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', handleSectionUnload);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handleDOMContentLoaded, { once: true });
  } else {
    initializeAll(document);
  }

  const destroy = () => {
    activeInstances.forEach((instance) => instance.destroy());
    activeInstances.clear();

    document.removeEventListener('DOMContentLoaded', handleDOMContentLoaded);
    document.removeEventListener('shopify:section:load', handleSectionLoad);
    document.removeEventListener('shopify:section:unload', handleSectionUnload);

    delete window.DeebAccessoryMotion;
  };

  window.DeebAccessoryMotion = Object.freeze({
    initialize: initializeAll,
    destroy,
    get activeInstances() {
      return activeInstances.size;
    },
  });
})();
